/**
 * SETTINGS.
 *
 * Reachable from every screen INCLUDING mid-wave, which is the point. WCAG
 * 2.5.4 requires that a motion-operated function also be operable
 * conventionally and that the motion response be disableable — a player who
 * granted permission and then found aiming painful must be able to get out
 * without leaving the room (ROUNDTABLE.md, A-2). That is why the input mode is
 * the first control on this sheet and not buried under "advanced".
 */

import { useEffect, useState } from 'react';
import type { AimController } from '../hunt/aim.js';
import { hasFinePointer, type Settings } from './settings.js';

interface Props {
  settings: Settings;
  aim: AimController;
  onChange: (next: Settings) => void;
  onClose: () => void;
  onRange: () => void;
}

export function SettingsSheet({ settings, aim, onChange, onClose, onRange }: Props) {
  const [, force] = useState(0);
  const gyro = aim.state.gyro;
  const fine = hasFinePointer();
  const mouse = settings.mode === 'mouse';

  const set = <K extends keyof Settings>(key: K, value: Settings[K]) => {
    onChange({ ...settings, [key]: value });
    force((n) => n + 1);
  };

  // Esc closes the sheet. It is also what opens it from the hunt, so the same
  // key gets you both in and out without looking for a Done button.
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        e.preventDefault();
        e.stopPropagation();
        onClose();
      }
    };
    window.addEventListener('keydown', onKey, true);
    return () => window.removeEventListener('keydown', onKey, true);
  }, [onClose]);

  return (
    <div className="sheet" role="dialog" aria-modal="true" aria-label="Settings" onClick={onClose}>
      <div className="sheet-inner" onClick={(e) => e.stopPropagation()}>
        <div className="row between">
          <h2 className="display m-0 fs-lg">Settings</h2>
          <button className="btn btn-ghost" onClick={onClose} aria-label="Close settings">
            Done
          </button>
        </div>

        <hr className="rule" />

        <p className="tiny muted m-0">How you aim</p>
        <div className="pill-group pill-wrap mt-xs" role="group" aria-label="Aiming mode">
          {/* Ordered by what this machine is likeliest to want, so the first
              pill is the right one on a phone and on a desktop alike. All
              three stay available on both: a touchscreen laptop is real, and
              so is a phone whose owner prefers a thumb. */}
          {fine && (
            <button aria-pressed={settings.mode === 'mouse'} onClick={() => set('mode', 'mouse')}>
              Mouse
            </button>
          )}
          <button aria-pressed={settings.mode === 'gyro'} onClick={() => set('mode', 'gyro')}>
            Point the phone
          </button>
          <button aria-pressed={settings.mode === 'touch'} onClick={() => set('mode', 'touch')}>
            {fine ? 'Drag' : 'Thumb'}
          </button>
          {!fine && (
            <button aria-pressed={settings.mode === 'mouse'} onClick={() => set('mode', 'mouse')}>
              Mouse
            </button>
          )}
        </div>
        <p className="tiny muted mt-xs mb-0">
          {settings.mode === 'mouse'
            ? 'The cursor is the sight. Click to fire, Space also fires, R reloads.'
            : settings.mode === 'gyro'
              ? gyro === 'live'
                ? 'Tilt and turn to aim. Tap anywhere to fire.'
                : gyro === 'denied'
                  ? 'Motion access was refused. Grant it in your browser settings, or use another mode.'
                  : gyro === 'unavailable'
                    ? 'No motion sensor on this device — one of the other two is the way here.'
                    : 'Ante up to switch this on.'
              : 'Drag anywhere to move the sight, tap to fire. Same accuracy, same leaderboard.'}
        </p>

        <div className="setting">
          <label className="label" htmlFor="sens">
            Sensitivity
          </label>
          <input
            id="sens"
            type="range"
            min={0.5}
            max={2}
            step={0.05}
            value={settings.sensitivity}
            disabled={mouse}
            onChange={(e) => set('sensitivity', Number(e.target.value))}
          />
          <span className="mono tiny switch">{settings.sensitivity.toFixed(2)}×</span>
        </div>
        {mouse && (
          <p className="tiny muted m-0">
            Mouse aiming is 1:1 with the screen, so there is nothing here to scale. Your pointer speed is
            your system&rsquo;s.
          </p>
        )}

        {!mouse && (
          <Toggle
            id="invert"
            label="Invert up/down"
            on={settings.invertY}
            onToggle={() => set('invertY', !settings.invertY)}
          />
        )}
        <Toggle
          id="firebtn"
          label="Show the fire button"
          on={settings.fireButton}
          onToggle={() => set('fireButton', !settings.fireButton)}
        />

        {!mouse && (
          <>
            <div className="row mt-sm">
              <button
                className="btn grow"
                onClick={() => {
                  aim.recenter();
                  force((n) => n + 1);
                }}
              >
                Re-centre the sight
              </button>
            </div>
            <p className="tiny muted mt-xs mb-0">
              Point where you want dead centre to be, then tap. Also on the sight itself during a wave.
            </p>
          </>
        )}

        <hr className="rule" />

        <Toggle id="sound" label="Sound" on={settings.sound} onToggle={() => set('sound', !settings.sound)} />
        <div className="setting">
          <label className="label" htmlFor="vol">
            Volume
          </label>
          <input
            id="vol"
            type="range"
            min={0}
            max={1}
            step={0.05}
            value={settings.volume}
            disabled={!settings.sound}
            onChange={(e) => set('volume', Number(e.target.value))}
          />
          <span className="mono tiny switch">{Math.round(settings.volume * 100)}%</span>
        </div>
        <Toggle id="music" label="Piano in the bar" on={settings.music} onToggle={() => set('music', !settings.music)} />
        <p className="tiny muted m-0">
          The bar has a piano; the woods have wind, birds and crickets. Both follow this switch pair.
        </p>

        <hr className="rule" />

        <button className="btn btn-wide" onClick={onRange}>
          Practice range
        </button>
        <p className="tiny muted mt-xs mb-0">
          Three deer, no score, no audience. The third one is a doe — that is on purpose.
        </p>
      </div>
    </div>
  );
}

function Toggle({ id, label, on, onToggle }: { id: string; label: string; on: boolean; onToggle: () => void }) {
  return (
    <div className="setting">
      <label className="label" htmlFor={id}>
        {label}
      </label>
      <button id={id} className="btn switch" role="switch" aria-checked={on} onClick={onToggle}>
        {on ? 'On' : 'Off'}
      </button>
    </div>
  );
}
